import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { startOfWeek as fnsStartOfWeek, format as fnsFormat, isSameDay as fnsIsSameDayOriginal, parseISO as fnsParseISO, isValid as fnsIsValid, getDay as fnsGetDay, addDays as fnsAddDays } from 'date-fns';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const getStartOfWeek = (date = new Date(), weekStartsOn = 1) => {
  if (!fnsIsValid(new Date(date))) return fnsStartOfWeek(new Date(), { weekStartsOn });
  return fnsStartOfWeek(new Date(date), { weekStartsOn });
};

export const getEndOfWeek = (date = new Date(), weekStartsOn = 1) => {
  const start = getStartOfWeek(date, weekStartsOn);
  return fnsAddDays(start, 6);
};

export const formatDate = (date, formatStr = "yyyy-MM-dd") => {
  if (!date) return "";
  let d;
  
  if (typeof date === 'string') {
    if (!date.includes('T') && !date.includes('Z') && /^\d{4}[-/]\d{2}[-/]\d{2}$/.test(date)) {
      const [year, month, day] = date.split(/[-/]/).map(Number);
      d = new Date(Date.UTC(year, month - 1, day));
    } else {
      d = fnsParseISO(date); 
    }
  } else if (date instanceof Date) {
    d = date;
  } else {
    try {
      d = new Date(date);
    } catch (e) {
      return "Invalid Date Input";
    }
  }
  
  if (!fnsIsValid(d)) return "Invalid Date"; 

  const correctedFormatStr = formatStr
    .replace(/YYYY/g, 'yyyy')
    .replace(/DD/g, 'dd')
    .replace(/(?<!d)D(?!d)/g, 'd');
  return fnsFormat(d, correctedFormatStr);
};


export const getWeekRangeText = (date = new Date(), weekStartsOn = 1) => {
  const start = getStartOfWeek(date, weekStartsOn);
  const end = getEndOfWeek(date, weekStartsOn);
  const startFormat = fnsFormat(start, "MMM d");
  const endFormat = fnsFormat(end, "MMM d, yyyy");
  return `${startFormat} – ${endFormat}`;
};

export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  
  const d1 = date1 instanceof Date ? date1 : fnsParseISO(String(date1));
  const d2 = date2 instanceof Date ? date2 : fnsParseISO(String(date2));

  if (!fnsIsValid(d1) || !fnsIsValid(d2)) {
      return false;
  }
  
  return fnsIsSameDayOriginal(d1, d2);
};

export const getDayOfWeekName = (date) => {
  if (!date) return "";
  let d;
  if (date instanceof Date) {
    d = date;
  } else {
    d = fnsParseISO(String(date));
  }
  if (!fnsIsValid(d)) return "Invalid Date";
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[d.getDay()];
};

export const getDayOfWeek = (date, weekStartsOn = 1) => {
  if (!date) return -1;
  let d;
  if (date instanceof Date) {
    d = date;
  } else if (typeof date === 'string') {
    if (!date.includes('T') && !date.includes('Z') && /^\d{4}[-/]\d{2}[-/]\d{2}$/.test(date)) {
      const [year, month, day] = date.split(/[-/]/).map(Number);
      d = new Date(Date.UTC(year, month - 1, day));
    } else {
      d = fnsParseISO(date);
    }
  } else {
    return -1;
  }

  if (!fnsIsValid(d)) return -1;
  
  let day = fnsGetDay(d); 

  if (weekStartsOn === 1) { // Monday is 0, Sunday is 6
    return (day === 0) ? 6 : day - 1;
  }
  // Sunday is 0, Saturday is 6 (default date-fns behavior)
  return day;
};

// Exact hours+minutes → decimal conversion. Intentionally NOT rounded: this
// value is billed (duration_decimal × rate), so rounding here silently drops
// pennies (e.g. 7h20m → 7.33 instead of 7.3333…). Round only at the display
// layer (.toFixed(2)) and only final money to the nearest penny.
export const hoursAndMinutesToDecimal = (hours, minutes) => {
  if (typeof hours !== 'number' || typeof minutes !== 'number') {
    return 0;
  }
  return hours + minutes / 60;
};

export const decimalToHoursAndMinutes = (decimal) => {
  if (typeof decimal !== 'number') {
    return { hours: 0, minutes: 0 };
  }
  const totalMinutes = Math.round(decimal * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
};

export const addDays = (date, amount) => {
  if (!date || !fnsIsValid(new Date(date))) return new Date();
  return fnsAddDays(new Date(date), amount);
}

export const parseISO = (dateString) => {
  return fnsParseISO(dateString);
};

/**
 * Replaces any $ symbol with £ to ensure UK currency consistency.
 * Especially useful for AI-generated strings.
 */
export const cleanCurrency = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(/\$/g, '£');
};

/**
 * Formats a number consistently as GBP.
 */
export const formatGBP = (amount) => {
  const value = typeof amount === 'number' ? amount : parseFloat(amount || 0);
  return `£${value.toFixed(2)}`;
};